import React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE, TypographySize } from 'mm-theme-configuration/dist/consts';
import { useTheme } from '../../../../theming/useTheme';
import { DropDownMenuLink } from './DropDownMenuLink';
import { ErrorBoundary } from '../../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { createStyleForBoxShadow } from '../../../../theming/boxShadow';
import { SemanticTagThemeProps } from '../../../../typography/semanticTags/semanticTags.utils';
import { BaseUnit } from '../../../../theming/baseUnitDefinition';
import { TranslatedFontSize } from '../../../../theming/fontSizeTranslator';
import { NavigationLink } from './primaryHeader/PrimaryHeader';

interface DropDownMenuProps {
  configThemeKey: string;
}

export interface DropDownMenuBaseThemeProps extends SemanticTagThemeProps {
  menuBackgroundColor: string;
  linkColor: string;
  hoverColor: string;
  activeColor: string;
  baseUnit: BaseUnit;
  boxShadowTypeNormal: string;
  fontSize?: TypographySize;
}

export interface DropDownMenuBaseProps {
  configThemeKey?: string;
  menuLinks: NavigationLink[];
  highlightActive?: boolean;
  activeEndpoint: string;
}

type ThemedDropDownMenu = DropDownMenuProps & DropDownMenuBaseProps;

const getStyle = (theme: DropDownMenuBaseThemeProps) => {
  const { baseUnit, menuBackgroundColor } = theme;

  return StyleSheet.create({
    menuWrapper: {
      position: 'absolute',
      top: '100%',
      width: '100%',
      minWidth: '160px',
      boxSizing: 'border-box',
      background: menuBackgroundColor,
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      zIndex: 100,
      [MEDIA_BREAKPOINTS.large]: {
        padding: `${baseUnit / 4}px ${baseUnit / 2}px`,
      },
    },
  });
};

const getLinksList = (
  menuLinks: NavigationLink[],
  linkColor: string,
  hoverColor: string,
  activeColor: string,
  baseUnit: BaseUnit,
  configThemeKey: string | undefined,
  activeEndpoint: string,
  fontSizesStyles: TranslatedFontSize,
  fontSize: TypographySize,
) => {
  const isActive = (navigationLink: NavigationLink) => navigationLink.link.href === activeEndpoint;
  return menuLinks.map((navigationLink, index) => (
    <DropDownMenuLink
      key={index}
      href={navigationLink.link.href}
      text={navigationLink.link.text}
      linkColor={linkColor}
      hoverColor={hoverColor}
      activeColor={activeColor}
      baseUnit={baseUnit}
      isActive={isActive(navigationLink)}
      configThemeKey={configThemeKey}
      fontSizesStyles={fontSizesStyles}
      fontSize={fontSize}
    />
  ));
};

export const DropDownMenu: React.FunctionComponent<ThemedDropDownMenu> = props => {
  const { menuLinks, configThemeKey, activeEndpoint } = props;
  const themeProps = useTheme<DropDownMenuBaseThemeProps>(configThemeKey);
  const { boxShadowTypeNormal, baseUnit, fontSizesStyles, linkColor, hoverColor, activeColor, fontSize = TYPOGRAPHY_SIZE.NORMAL } = themeProps;
  const style = getStyle(themeProps);
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal });
  return (
    <ErrorBoundary>
      <ul className={css(style.menuWrapper, boxShadowStyle.style)}>
        {getLinksList(menuLinks, linkColor, hoverColor, activeColor, baseUnit, configThemeKey, activeEndpoint, fontSizesStyles, fontSize)}
      </ul>
    </ErrorBoundary>
  );
};
